import { useEffect, useMemo, useState } from 'react';
import ChartCard from '../components/ChartCard';
import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import ExportButtons from '../components/ExportButtons';
import LoadingState from '../components/LoadingState';
import StatCard from '../components/StatCard';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useDateRange } from '../contexts/DateRangeContext';
import { getTeamMemberDetailRows } from '../services/reportService';
import { exportSheetsToXlsx } from '../utils/exporters';
import { formatNumber, formatPercent } from '../utils/formatters';
import {
  PRIMARY_MILESTONES,
  ROLE_PERFORMANCE_DEFINITIONS,
  asNumber,
  buildIndividualKpiSummary,
  buildMemberOptions,
  buildQualityStatusRowsFromMetric,
  buildRolePerformanceSections,
  buildTatStatusRowsFromMetric,
  countBy,
  assignmentRowsForMember,
  memberKey,
  milestoneKeys,
  roleRowsForMember,
  rowMatchesMemberAnyRole,
  rowMatchesMemberForRole,
  serviceLabel,
  uniqueProjectCount,
} from '../utils/individualPerformance';

const NO_MEMBER = '';

function formatTatValue(value) {
  return value === null || value === undefined ? '—' : Number(value).toFixed(2);
}

function buildIdleRows(rows) {
  return PRIMARY_MILESTONES.map((milestoneType) => {
    const scopedRows = rows.filter((row) => row.milestone_type === milestoneType);
    const readyForDesign = scopedRows.map((row) => asNumber(row.ready_for_design_tat_hours)).filter((value) => value > 0);
    const readyForPe = scopedRows.map((row) => asNumber(row.ready_for_pe_tat_hours)).filter((value) => value > 0);
    const average = (values) => (values.length ? Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 100) / 100 : 0);
    return {
      milestone_type: milestoneType,
      avg_ready_for_design: average(readyForDesign),
      avg_ready_for_pe: average(readyForPe),
      request_count: scopedRows.length,
    };
  });
}

function buildAssignmentPreviewRows(rows, selectedMember) {
  return assignmentRowsForMember(rows, selectedMember, 300);
}


const percentColumn = { key: 'percent_of_total', label: '% of Total', align: 'right', render: formatPercent };

const roleMetricColumns = [
  { key: 'milestone_type', label: 'Milestone Type' },
  { key: 'output_count', label: 'Output', align: 'right', render: formatNumber },
  { key: 'quality_passed', label: 'Quality Passed', align: 'right', render: (value, row) => (row.quality_applicable ? formatNumber(value) : 'N/A') },
  { key: 'quality_failed', label: 'Quality Error / Failed', align: 'right', render: (value, row) => (row.quality_applicable ? formatNumber(value) : 'N/A') },
  { key: 'quality_blank', label: 'Quality Blank', align: 'right', render: (value, row) => (row.quality_applicable ? formatNumber(value) : 'N/A') },
  { key: 'quality_percent', label: 'Quality %', align: 'right', render: (value, row) => (row.quality_applicable && value !== null && value !== undefined ? formatPercent(value) : 'N/A') },
  { key: 'tat_threshold_label', label: 'TAT Target', align: 'right' },
  { key: 'tat_on_time', label: 'TAT On-Time', align: 'right', render: formatNumber },
  { key: 'tat_late', label: 'TAT Late', align: 'right', render: formatNumber },
  { key: 'tat_on_time_percent', label: 'TAT On-Time %', align: 'right', render: formatPercent },
];

const statusColumns = [
  { key: 'status', label: 'Status' },
  { key: 'project_count', label: 'Project Count', align: 'right', render: formatNumber },
  percentColumn,
];

const assignmentColumns = [
  { key: 'project_name', label: 'Project' },
  { key: 'milestone_date', label: 'Date' },
  { key: 'milestone_type', label: 'Milestone Type' },
  { key: 'assigned_role', label: 'Assigned Role' },
  { key: 'service_type', label: 'Service Type' },
  { key: 'tat_hours', label: 'TAT', align: 'right', render: formatTatValue },
  { key: 'target_tat_label', label: 'Target TAT', align: 'right' },
  { key: 'qa_result', label: 'Milestone QA Result' },
  { key: 'cre_restamp_reason', label: 'CRE Restamp Reason' },
  { key: 'status', label: 'Role TAT Status' },
];

function RolePerformanceSection({ section }) {
  const { definition, roleRows, metricRows, serviceRows } = section;
  const initialMetric = metricRows.find((row) => row.milestone_type === 'Initial Project Delivery');
  const restampMetric = metricRows.find((row) => row.milestone_type === 'Restamp Required');
  const initialTatRows = buildTatStatusRowsFromMetric(initialMetric);
  const restampTatRows = buildTatStatusRowsFromMetric(restampMetric);
  const initialQualityRows = buildQualityStatusRowsFromMetric(initialMetric, definition);
  const restampQualityRows = buildQualityStatusRowsFromMetric(restampMetric, definition);
  const outputRows = metricRows.map((row) => ({ milestone_type: row.milestone_type, output_count: row.output_count }));

  return (
    <section className="role-section">
      <div className="section-heading section-bar soft-bar">
        <h3>{definition.sectionTitle}</h3>
        <span>{roleRows.length} assignment{roleRows.length === 1 ? '' : 's'}</span>
      </div>

      <div className="panel workbook-table-panel">
        <p className="section-note"><strong>Output:</strong> {definition.outputRule}</p>
        <p className="section-note"><strong>Quality:</strong> {definition.qualityRule}</p>
        <p className="section-note"><strong>TAT:</strong> Initial target = {metricRows.find((row) => row.milestone_type === 'Initial Project Delivery')?.tat_threshold_label || 'N/A'}; Restamp target = {metricRows.find((row) => row.milestone_type === 'Restamp Required')?.tat_threshold_label || 'N/A'} using {definition.tatLabel}.</p>
        <DataTable columns={roleMetricColumns} rows={metricRows} />
      </div>

      <div className="grid-two">
        {serviceRows.length > 0 && (
          <ChartCard
            title={`${definition.label} — Service Type Mix`}
            data={serviceRows}
            xKey="service_type"
            type="donut"
            nameKey="service_type"
            valueKey="project_count"
            height={430}
            showLegend
            minCalloutPercent={0.02}
            maxCallouts={3}
          />
        )}
        {outputRows.length > 0 && (
          <ChartCard
            title={`${definition.label} — Output by Milestone`}
            data={outputRows}
            xKey="milestone_type"
            type="groupedBar"
            series={[{ key: 'output_count', name: 'Output' }]}
            height={320}
            showLegend={false}
          />
        )}
      </div>

      {(initialQualityRows.length > 0 || restampQualityRows.length > 0) && (
        <div className="grid-two small-chart-grid">
          {initialQualityRows.length > 0 && (
            <div className="panel workbook-table-panel ontime-detail-block">
              <ChartCard title={`${definition.label} — Initial Project Delivery: Quality`} data={initialQualityRows} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={310} showLegend minCalloutPercent={0.01} maxCallouts={3} />
              <DataTable columns={statusColumns} rows={initialQualityRows} />
            </div>
          )}
          {restampQualityRows.length > 0 && (
            <div className="panel workbook-table-panel ontime-detail-block">
              <ChartCard title={`${definition.label} — Restamp Required: Quality`} data={restampQualityRows} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={310} showLegend minCalloutPercent={0.01} maxCallouts={3} />
              <DataTable columns={statusColumns} rows={restampQualityRows} />
            </div>
          )}
        </div>
      )}

      {(initialTatRows.length > 0 || restampTatRows.length > 0) && (
        <div className="grid-two small-chart-grid">
          {initialTatRows.length > 0 && (
            <div className="panel workbook-table-panel ontime-detail-block">
              <ChartCard title={`${definition.label} — Initial Project Delivery: TAT On-Time vs Late`} data={initialTatRows} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={310} showLegend={false} minCalloutPercent={0.01} maxCallouts={2} />
              <DataTable columns={statusColumns} rows={initialTatRows} />
            </div>
          )}
          {restampTatRows.length > 0 && (
            <div className="panel workbook-table-panel ontime-detail-block">
              <ChartCard title={`${definition.label} — Restamp Required: TAT On-Time vs Late`} data={restampTatRows} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={310} showLegend={false} minCalloutPercent={0.01} maxCallouts={2} />
              <DataTable columns={statusColumns} rows={restampTatRows} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function TeamMemberDetailPage() {
  const { activeUpload, loading: uploadLoading } = useActiveUpload();
  const { startDate, endDate } = useDateRange();
  const [rows, setRows] = useState([]);
  const [selectedMember, setSelectedMember] = useState(NO_MEMBER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeUpload?.id) return;
      setLoading(true);
      setError('');
      try {
        const detailRows = await getTeamMemberDetailRows(activeUpload.id, startDate, endDate);
        if (!cancelled) {
          setRows(detailRows || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeUpload?.id, startDate, endDate]);

  const memberOptions = useMemo(() => buildMemberOptions(rows), [rows]);

  useEffect(() => {
    if (!memberOptions.length) {
      setSelectedMember(NO_MEMBER);
      return;
    }
    if (!selectedMember || !memberOptions.some((name) => memberKey(name) === memberKey(selectedMember))) {
      setSelectedMember(memberOptions[0]);
    }
  }, [memberOptions, selectedMember]);

  const roleSummaryRows = useMemo(() => roleRowsForMember(rows, selectedMember), [rows, selectedMember]);
  const nonZeroRoleSummaryRows = useMemo(() => roleSummaryRows.filter((row) => row.total > 0), [roleSummaryRows]);
  const selectedRows = useMemo(() => rows.filter((row) => rowMatchesMemberAnyRole(row, selectedMember)), [rows, selectedMember]);
  const milestoneMixRows = useMemo(() => countBy(selectedRows, (row) => milestoneKeys.has(row.milestone_type) ? row.milestone_type : 'Other', 'milestone_type'), [selectedRows]);
  const serviceMixRows = useMemo(() => countBy(selectedRows, serviceLabel, 'service_type'), [selectedRows]);
  const rolePerformanceSections = useMemo(() => buildRolePerformanceSections(rows, selectedMember), [rows, selectedMember]);
  const idleRows = useMemo(() => buildIdleRows(selectedRows), [selectedRows]);
  const idleRowsWithData = useMemo(() => idleRows.filter((row) => asNumber(row.request_count) > 0), [idleRows]);
  const assignmentPreviewRows = useMemo(() => buildAssignmentPreviewRows(rows, selectedMember), [rows, selectedMember]);

  const kpis = useMemo(() => {
    const initial = selectedRows.filter((row) => row.milestone_type === 'Initial Project Delivery').length;
    const restamp = selectedRows.filter((row) => row.milestone_type === 'Restamp Required').length;
    const totalRoleAssignments = nonZeroRoleSummaryRows.reduce((sum, row) => sum + row.total, 0);
    const roleCounts = ROLE_PERFORMANCE_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.key] = rows.filter((row) => rowMatchesMemberForRole(row, definition.role, selectedMember)).length;
      return acc;
    }, {});
    return [
      { key: 'unique_projects', label: 'Unique Projects', value: uniqueProjectCount(selectedRows), format: 'number' },
      { key: 'total_milestones', label: 'Total Milestone Rows', value: selectedRows.length, format: 'number' },
      { key: 'role_assignments', label: 'Role Assignments', value: totalRoleAssignments, format: 'number' },
      { key: 'initial', label: 'Initial Project Delivery', value: initial, format: 'number' },
      { key: 'restamp', label: 'Restamp Required', value: restamp, format: 'number' },
      { key: 'design_count', label: 'Design Output', value: roleCounts.design || 0, format: 'number' },
      { key: 'design_review_count', label: 'Design Review Output', value: roleCounts.design_review || 0, format: 'number' },
      { key: 'pe_count', label: 'PE Output', value: roleCounts.pe || 0, format: 'number' },
      { key: 'ee_count', label: 'EE Output', value: roleCounts.ee || 0, format: 'number' },
    ].filter((item) => asNumber(item.value) > 0);
  }, [selectedRows, nonZeroRoleSummaryRows, rows, selectedMember]);

  const individualKpiSummary = useMemo(() => buildIndividualKpiSummary(rolePerformanceSections), [rolePerformanceSections]);

  const exportRows = useMemo(() => assignmentPreviewRows.map((row) => ({
    Project: row.project_name,
    Date: row.milestone_date,
    'Milestone Type': row.milestone_type,
    'Assigned Role': row.assigned_role,
    'Service Type': row.service_type,
    TAT: row.tat_hours,
    'Target TAT': row.target_tat_label,
    'Milestone QA Result': row.qa_result,
    'CRE Restamp Reason': row.cre_restamp_reason,
    Status: row.status,
  })), [assignmentPreviewRows]);

  const workbookSheets = useMemo(() => {
    const roleMetricSheet = [];
    rolePerformanceSections.forEach((section) => {
      section.metricRows.forEach((row) => {
        roleMetricSheet.push({
          Role: section.definition.label,
          'Milestone Type': row.milestone_type,
          Output: row.output_count,
          'Quality Passed': row.quality_passed,
          'Quality Error / Failed': row.quality_failed,
          'Quality Blank': row.quality_blank,
          'Quality %': row.quality_percent,
          'TAT Target': row.tat_threshold_label,
          'TAT On-Time': row.tat_on_time,
          'TAT Late': row.tat_late,
          'TAT On-Time %': row.tat_on_time_percent,
        });
      });
    });

    return {
      KPI: individualKpiSummary.map((item) => ({
        Metric: item.label,
        Value: item.format === 'hoursPerDay' ? `${item.value} h/day` : item.value,
        Detail: item.helper,
      })),
      'Role Metrics': roleMetricSheet,
      Assignments: exportRows,
    };
  }, [individualKpiSummary, rolePerformanceSections, exportRows]);

  if (uploadLoading) return <LoadingState message="Loading active upload..." />;
  if (!activeUpload) return <EmptyState title="No active report" message="Ask an admin to upload and activate a Project Milestone Report." />;

  return (
    <div className="page-stack team-member-detail-page">
      <div className="page-title-row">
        <div>
          <h2>Individual Team Member Performance</h2>
          <p>Select one team member to view Design, Design Review, PE, and EE output, quality, and TAT metrics. Date range: {startDate} to {endDate}</p>
        </div>
        <ExportButtons
          onXlsx={() => exportSheetsToXlsx(workbookSheets, `team-member-detail-${selectedMember || 'member'}-${startDate}-to-${endDate}.xlsx`)}
        />
      </div>

      <ErrorMessage message={error} />

      <div className="panel member-selector-panel">
        <label>
          <span>Team Member</span>
          <select value={selectedMember} onChange={(event) => setSelectedMember(event.target.value)} disabled={loading || !memberOptions.length}>
            {!memberOptions.length && <option value="">No team members found</option>}
            {memberOptions.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        <p>
          A selected member includes rows where their name appears in any role column. If a role cell has names joined by <strong>&amp;</strong>, the page treats each name as part of that assignment.
        </p>
      </div>

      {loading ? <LoadingState message="Loading individual performance..." /> : selectedMember ? (
        <>
          <div className="stat-grid individual-stat-grid">
            {kpis.map((item) => <StatCard key={item.key} item={item} />)}
          </div>

          {rolePerformanceSections.length > 0 && (
            <section className="role-section">
              <div className="section-heading section-bar soft-bar">
                <h3>KPI</h3>
                <span>Output Quantity, Quality, and Turnaround Time</span>
              </div>
              <div className="stat-grid individual-kpi-summary-grid">
                {individualKpiSummary.map((item) => <StatCard key={item.key} item={item} />)}
              </div>
            </section>
          )}

          {selectedRows.length > 0 && (milestoneMixRows.length > 0 || serviceMixRows.length > 0) && (
            <section className="role-section">
              <div className="section-heading section-bar soft-bar">
                <h3>Overall Assignment Mix</h3>
                <span>Unique milestone rows where {selectedMember} appears in any role</span>
              </div>
              <div className="grid-two">
                {milestoneMixRows.length > 0 && <ChartCard title="Milestone Type Mix" data={milestoneMixRows} xKey="milestone_type" type="donut" nameKey="milestone_type" valueKey="project_count" height={430} showLegend minCalloutPercent={0.02} maxCallouts={3} />}
                {serviceMixRows.length > 0 && <ChartCard title="Service Type Mix" data={serviceMixRows} xKey="service_type" type="donut" nameKey="service_type" valueKey="project_count" height={430} showLegend minCalloutPercent={0.02} maxCallouts={3} />}
              </div>
            </section>
          )}

          {rolePerformanceSections.map((section) => <RolePerformanceSection key={section.definition.key} section={section} />)}

          {idleRowsWithData.length > 0 && (
            <section className="role-section">
              <div className="section-heading section-bar soft-bar">
                <h3>Average Idle Time</h3>
                <span>Rows where {selectedMember} appears in any role</span>
              </div>
              <div className="grid-two">
                <div className="panel workbook-table-panel">
                  <DataTable
                    columns={[
                      { key: 'milestone_type', label: 'Milestone Type' },
                      { key: 'avg_ready_for_design', label: 'Avg Ready for Design (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                      { key: 'avg_ready_for_pe', label: 'Avg Ready for PE (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                      { key: 'request_count', label: 'Request Count', align: 'right', render: formatNumber },
                    ]}
                    rows={idleRowsWithData}
                  />
                </div>
                <ChartCard title="Average Idle Time" data={idleRowsWithData} xKey="milestone_type" type="groupedBar" series={[{ key: 'avg_ready_for_design', name: 'Avg Ready for Design (hrs)' }, { key: 'avg_ready_for_pe', name: 'Avg Ready for PE (hrs)' }]} height={320} />
              </div>
            </section>
          )}

          {assignmentPreviewRows.length > 0 && (
            <section className="role-section">
              <div className="section-heading section-bar soft-bar">
                <h3>Assignment Detail</h3>
                <span>Showing up to 300 rows</span>
              </div>
              <div className="panel workbook-table-panel">
                <DataTable columns={assignmentColumns} rows={assignmentPreviewRows} />
              </div>
            </section>
          )}
        </>
      ) : (
        <EmptyState title="No team member found" message="The active report does not contain team member names for the selected date range." />
      )}
    </div>
  );
}
